import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { Ac, AcDto, Committee, CommitteeDto, IResponse, User } from '@/types';
import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, SimpleChanges, ViewChild, OnChanges } from '@angular/core';
import { getMembersDataTableStructure } from './utils/members-data-table-object';
import { CommitteeRepository } from '@/store/committee-state/committee-state.repository';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { getMemberAddDialogFormObject } from './utils/member-add-dialog-object';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { ProfileService } from '@/pages/profile/profile.service';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getMemberFormObject } from './utils/member-form-object';
import { TranslateService } from '@ngx-translate/core';
import { CommitteeService } from '../committee/committee.service';
import { MessageService } from 'primeng/api';
import { forkJoin, map, Observable } from 'rxjs';
import { getMemberDeleteObject } from './utils/member-delete-dialog-object';
import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { ContractingAuthorityService } from '../contracting-authority/contracting-authority.service';
import { AuthorizationService } from '@/services/authorization.service';
import { UserRole } from '@/types/user.types';

@Component({
  selector: 'sm-members',
  imports: [CommonModule, DataTableBuilderComponent, DialogComponent],
  templateUrl: './members.component.html',
  styleUrl: './members.component.scss',
})
export class MembersComponent implements OnInit, OnChanges {
  //services
  committeeRepository = inject(CommitteeRepository);
  contractingAuthorityRepository = inject(ContractingAuthorityRepository);
  committeService = inject(CommitteeService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  profileService = inject(ProfileService);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.members.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.members.breadcrumb.members',
      routerLink: ['/settings', 'members'],
    },
  ];

  //input
  @Input() committeeId?: number;
  @Input() acId?: number;
  @Input() members?: User[];
  @Input() isInspectMode?: boolean;
  @Input() isConsellierMode: boolean = false;

  isAddAcDisabled$: Observable<boolean> = this.contractingAuthorityRepository.getObservable<SelectOption[]>('membersSelected').pipe(
    map(members => {
      return (members?.length ?? 0) === 0;
    })
  );

  isAddComDisabled$: Observable<boolean> = this.committeeRepository.getObservable<SelectOption[]>('membersSelected').pipe(
    map(members => {
      return (members?.length ?? 0) === 0;
    })
  );

  membersOptions?: { label: string; value: number }[];

  @ViewChild('dialog') membersComponent!: DialogComponent;
  memberDialog?: DialogObject;

  @ViewChild('delete') deleteDialogComponent!: DialogComponent;
  deleteDialog?: DialogObject;

  dialogFormObject: DynamicForm = getFormBuilderObjectFactory();

  data: User[] = [];
  dataTableObject?: DynamicDataTable;
  loading = false;
  isDialogOpen = false;

  rowActions: DataTableRowActions = {
    deleteAction:
      this.isInspectMode || !this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
        ? undefined
        : (row: any) => {
            // Set the member to delete in the appropriate repository
            if (this.committeeId) {
              this.committeeRepository.set('memberToDelete', row);
            } else {
              this.contractingAuthorityRepository.set('memberToDelete', row);
            }

            this.deleteDialog = getMemberDeleteObject({
              action: () => this.deleteMember(),
              toggleVisibility: () => this.deleteDialogComponent.toggleVisible(),
            });
            this.deleteDialogComponent.toggleVisible();
          },
  };

  ngOnInit(): void {
    this.dataTableObject = getMembersDataTableStructure({
      store: this.committeeId ? this.committeeRepository : this.contractingAuthorityRepository,
      isConsellierMode: this.isConsellierMode,
      createAction:
        this.isInspectMode || !this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
          ? undefined
          : () => {
              this.isDialogOpen = true;
              setTimeout(() => {
                this.membersComponent.toggleVisible();
              });
            },
      rowActions: this.isInspectMode ? undefined : this.rowActions,
    });
    this.loadUsers();
  }

  loadUsers() {
    this.profileService.getUserList().subscribe(users => {
      this.data = users;
      this.refreshMembers();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['members'] && this.members) {
      this.refreshMembers();
    }
  }

  updateMembers() {
    const isCommittee = !!this.committeeId;
    const repo = isCommittee ? this.committeeRepository : this.contractingAuthorityRepository;
    const type = isCommittee ? 'COM' : 'AC';
    const id = isCommittee ? this.committeeId : this.acId;

    // Get the newly selected IDs from the form selection (what the user just picked in the dialog)
    const selectedItems: any[] = (repo.get<any[]>('membersSelected') || []).map((item: any) => item.code);

    // Read existing IDs directly from the server response — the source of truth
    const existingConsellierIds: any[] = (repo.getNested<User[]>('response', 'conselliers') || []).map(u => u.id);
    const existingUserIds: any[] = (repo.getNested<User[]>('response', 'users') || [])
      .map(u => u.id)
      .filter(id => !existingConsellierIds.includes(id));

    // Merge newly selected IDs only into the correct field, leave the other untouched
    const mergedUserIds = this.isConsellierMode
      ? existingUserIds
      : Array.from(new Set([...existingUserIds, ...selectedItems]));

    const mergedConsellierIds = this.isConsellierMode
      ? Array.from(new Set([...existingConsellierIds, ...selectedItems]))
      : existingConsellierIds;

    // Update the DTO with the correct merged values
    repo.setNested('dto', 'userIds', mergedUserIds);
    if (isCommittee) {
      repo.setNested('dto', 'consellierIds', mergedConsellierIds);
    }

    const dto = repo.getNested<CommitteeDto | AcDto>('dto');

    const request$: Observable<IResponse<Committee>> | Observable<IResponse<Ac>> = isCommittee
      ? this.committeService.updateCommittee(id as number, this.buildFormData(dto, type))
      : this.contractingAuthorityService.updateContractingAuthority(id as number, this.buildFormData(dto, type));

    (request$ as Observable<any>).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.isConsellierMode?'Consellier ajouté':'Member ajouté',
          detail: this.isConsellierMode?this.translate.instant('settings.consellier.message.success.added'):this.translate.instant('settings.members.message.success.added'),
        });
        this.membersComponent.toggleVisible();
        if (isCommittee) {
          this.loadDataCommittee();
        }
        if (!isCommittee) {
          this.loadDataContractingAuthority();
          repo.set('membersSelected', []);
        }
      },

      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || (this.isConsellierMode ? this.translate.instant('settings.consellier.message.error.added') : this.translate.instant('settings.members.message.error.added')),
        });
      },
    });
  }


  deleteMember() {
    const { isCommittee, repo, type, id } = this.prepareValuesForMembers();
    const memberToDelete = repo.get<User>('memberToDelete');
    const existingConsellierIds = (repo.getNested<User[]>('response', 'conselliers') || []).map(u => u.id);
    const existingUserIds = (repo.getNested<User[]>('response', 'users') || [])
      .map(u => u.id)
      .filter(id => !existingConsellierIds.includes(id));

    if (!memberToDelete) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.isConsellierMode ? this.translate.instant('settings.consellier.message.error.deleted') : this.translate.instant('settings.members.message.error.deleted'),
      });
      return;
    }

    if (this.isConsellierMode) {
      repo.setNested(
        'dto',
        'consellierIds',
        existingConsellierIds.filter(id => id !== memberToDelete.id)
      );
      repo.setNested('dto', 'userIds', existingUserIds);
    } else {
      repo.setNested(
        'dto',
        'userIds',
        existingUserIds.filter(id => id !== memberToDelete.id)
      );
      if (isCommittee) {
        repo.setNested('dto', 'consellierIds', existingConsellierIds);
      }
    }

    const dto = repo.getNested<CommitteeDto | AcDto>('dto');

    const request$: Observable<IResponse<Committee>> | Observable<IResponse<Ac>> = isCommittee
      ? this.committeService.updateCommittee(id as number, this.buildFormData(dto, type))
      : this.contractingAuthorityService.updateContractingAuthority(id as number, this.buildFormData(dto, type));

    (request$ as Observable<any>).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.isConsellierMode ? 'Conseiller supprimé' : 'Membre supprimé',
          detail: this.isConsellierMode ? this.translate.instant('settings.consellier.message.success.deleted') : this.translate.instant('settings.members.message.success.deleted'),
        });
        this.deleteDialogComponent.toggleVisible();
        if (isCommittee) {
          this.loadDataCommittee();
        }
        if (!isCommittee) {
          this.loadDataContractingAuthority();
        }
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || (this.isConsellierMode ? this.translate.instant('settings.consellier.message.error.deleted') : this.translate.instant('settings.members.message.error.deleted')),
        });
      },
    });
  }

  loadDataCommittee() {
    this.committeService.getOneCommittee(this.committeeId as number).subscribe({
      next: response => {
        this.committeeRepository.set('response', response);
        this.committeeRepository.set('dto', {
          nom: response.nom,
          decretCreation: response.decretCreation,
          responsableId: response.responsableId,
          special: response.special,
          ancrage: response.ancrage,
          theAdresse: {
            id: (response as any)?.theAdresse?.id || (response as any)?.adresseId,
            email: (response as any)?.theAdresse?.email || '',
            tel1: (response as any)?.theAdresse?.tel1 || '',
            tel2: (response as any)?.theAdresse?.tel2 || '',
            fax: (response as any)?.theAdresse?.fax || '',
            adresse: (response as any)?.theAdresse?.adresse || '',
            compAdresse: (response as any)?.theAdresse?.compAdresse || '',
            codePostal: (response as any)?.theAdresse?.codePostal || '',
            ville: (response as any)?.theAdresse?.ville || '',
            paysId: (response as any)?.theAdresse?.pays?.id || null,
          },
          userIds: response.users
            .map(user => user.id)
            .filter(id => !(response.conselliers || []).some(c => c.id === id)),
          consellierIds: response.conselliers ? response.conselliers.map(user => user.id) : [],
          contact: response.contact,
          comTypeId: response.comType?.id,
        });
        this.committeeRepository.set('membersSelected', []);
        this.loadUsers();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.members.message.error.loading'),
        });
        this.committeeRepository.set('membersSelected', []);
      },
    });
  }

  loadDataContractingAuthority() {
    this.contractingAuthorityService.getOneContractingAuthorityBatch(this.acId as number).subscribe({
      next: response => {
        this.contractingAuthorityRepository.set('response', response);
        this.contractingAuthorityRepository.set('dto', {
          nom: response.nom ?? '',
          ancrage: response.tutel?.ancrage ?? null,
          contact: response.contact ?? null,
          userIds: Array.isArray(response.users) ? response.users.map(user => user.id.toString()) : [],
          tutelId: response.tutel?.id ?? null,
          email: response.email ?? '',
          logo: response.logo ?? null,
          logoFile: undefined,
          istTutel: response.istTutel ?? false,
          comId: response.com?.id ?? null,
          typeAffCommission: response.typeAffCommission ?? null,
          adresse: {
            id: response.adresse?.id,
            tel1: response.adresse?.tel1 ?? '',
            tel2: response.adresse?.tel2 ?? '',
            fax: response.adresse?.fax ?? '',
            adresse: response.adresse?.adresse ?? '',
            compAdresse: response.adresse?.compAdresse ?? '',
            codePostal: response.adresse?.codePostal ?? '',
            ville: response.adresse?.ville ?? '',
            paysId: response.adresse?.pays && typeof (response.adresse.pays as any).id === 'number' ? (response.adresse.pays as any).id : null,
          },
        });
        this.loadUsers();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.members.message.error.loading'),
        });
      },
    });
  }

  refreshMembers() {
    const { repo, isAddDisabled$ } = this.prepareValuesForMembers();

    const users = repo.getNested<User[]>('response', 'users') || [];
    const conselliers = repo.getNested<User[]>('response', 'conselliers') || [];
    const assignedMembers = [...users, ...conselliers];

    this.membersOptions =
      this.data
        ?.filter(user => !assignedMembers.some(member => member.id === user.id))
        .map(user => ({
          label: user.email,
          value: user.id,
        })) || [];

    repo.set('membersSelected', []);

    this.dialogFormObject = getMemberFormObject({
      store: repo,
      membersOptions: this.membersOptions,
      isConsellierMode: this.isConsellierMode,
    });

    this.memberDialog = getMemberAddDialogFormObject({
      formObject: this.dialogFormObject,
      action: () => this.updateMembers(),
      isAddDisabled: isAddDisabled$,
      isConsellierMode: this.isConsellierMode,
      toggleVisibility: () => {
        this.isDialogOpen = false;
        this.membersComponent.toggleVisible();
        repo.set('membersSelected', []);
      },
    });
  }

  private buildFormData(dto: CommitteeDto | AcDto, type: 'AC' | 'COM'): FormData {
    const formData = new FormData();
    Object.entries(dto).forEach(([key, value]) => {
      if (type === 'COM') {
        if (key !== 'theAdresse' && key !== 'userIds' && key !== 'consellierIds' && value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value)) {
            value.forEach((item: any, index: number) => {
              formData.append(`${key}[${index}]`, item);
            });
          } else {
            formData.append(key, String(value));
          }
        }
      }
      if (type === 'AC') {
        if (key !== 'adresse' && key !== 'userIds' && value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value)) {
            value.forEach((item: any, index: number) => {
              formData.append(`${key}[${index}]`, item);
            });
          } else {
            formData.append(key, String(value));
          }
        }
      }
    });
    if (type === 'COM' && (dto as CommitteeDto).theAdresse) {
      formData.append('theAdresse', JSON.stringify((dto as CommitteeDto).theAdresse));
    }
    if (type === 'AC' && (dto as AcDto).adresse) {
      formData.append('adresse', JSON.stringify((dto as AcDto).adresse));
    }
    if (Array.isArray(dto.userIds)) {
      dto.userIds.forEach((id: any) => formData.append(`userIds`, id.toString()));
    }
    const committeeDto = dto as CommitteeDto;
    if (Array.isArray(committeeDto.consellierIds)) {
      committeeDto.consellierIds.forEach((id: string) => formData.append(`consellierIds`, id.toString()));
    }
    return formData;
  }

  prepareValuesForMembers() {
    const isCommittee = !!this.committeeId;
    const repo = isCommittee ? this.committeeRepository : this.contractingAuthorityRepository;
    const type = isCommittee ? 'COM' : 'AC';
    const id = isCommittee ? this.committeeId : this.acId;

    const newMembers: any[] = this.isConsellierMode
      ? repo.getNested('dto', 'consellierIds') || []
      : repo.getNested('dto', 'userIds') || [];
    const oldMemberIds: any[] = (repo.getNested<User[]>('response', this.isConsellierMode ? 'conselliers' : 'users') || []).map(member => member.id);

    if (this.isConsellierMode) {
      repo.setNested('dto', 'consellierIds', Array.from(new Set([...newMembers, ...oldMemberIds])));
    } else {
      repo.setNested('dto', 'userIds', Array.from(new Set([...newMembers, ...oldMemberIds])));
    }

    const isAddDisabled$ = isCommittee ? this.isAddComDisabled$ : this.isAddAcDisabled$;

    return {
      isCommittee,
      repo,
      type: type as 'COM' | 'AC',
      id,
      isAddDisabled$,
    };
  }
}
